import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useContent } from "@/context/ContentContext";
import { Kicker, SectionTitle } from "./Reveal";

function Panel({ q, title, desc, img, i }) {
  return (
    <article
      className="group relative h-[52vh] w-[82vw] shrink-0 overflow-hidden rounded-[10px] md:h-[56vh] md:w-[40vw]"
      data-testid={`year-quarter-${i}`}
      data-cursor="view"
    >
      <img
        src={img || "/images/texture.jpg"}
        alt={title}
        loading="lazy"
        className="img-editorial absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent opacity-90 transition-opacity duration-700 group-hover:opacity-80" />
      <div className="absolute inset-x-0 bottom-0 p-8 md:p-10">
        <p className="font-display text-5xl font-bold leading-none text-terra md:text-6xl">{q}</p>
        <h3 className="mt-4 font-display text-2xl font-semibold text-white">{title}</h3>
        <p className="mt-2 max-w-sm leading-relaxed text-[#CFE3D9]">{desc}</p>
      </div>
      <span className="absolute right-6 top-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-sand/60">
        0{i + 1} / 04
      </span>
    </article>
  );
}

export default function YearSection() {
  const c = useContent();
  const quarters = c.pairs("year", "quarters");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-62%"]);
  const progress = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section ref={ref} className="relative bg-pine text-white" style={{ height: "320vh" }} data-testid="year-section">
      <div className="sticky top-0 flex h-screen flex-col overflow-hidden">
        <div className="mx-auto w-full max-w-[1200px] px-5 pb-8 pt-28">
          <Kicker light>{c.get("year", "kicker")}</Kicker>
          <SectionTitle light className="max-w-3xl md:text-4xl">
            {c.get("year", "title")}
          </SectionTitle>
        </div>
        <motion.div
          style={{ x }}
          className="flex flex-1 items-end gap-5 pl-5 md:gap-6 md:pl-[max(1.25rem,calc((100vw-1200px)/2+1.25rem))]"
        >
          {quarters.map(([q, title, desc, img], i) => (
            <Panel key={i} q={q} title={title} desc={desc} img={img} i={i} />
          ))}
          <div className="flex h-[52vh] w-[70vw] shrink-0 flex-col justify-end pr-10 md:h-[56vh] md:w-[38vw]">
            <p
              className="font-display text-2xl font-medium leading-snug text-white md:text-3xl"
              data-testid="year-note"
            >
              {c.get("year", "note")}
            </p>
          </div>
        </motion.div>
        <div className="mx-auto w-full max-w-[1200px] px-5 pb-8 pt-6">
          <div className="h-px w-full bg-sand/20">
            <motion.div style={{ width: progress }} className="h-px bg-terra" />
          </div>
        </div>
      </div>
    </section>
  );
}
